import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import ObsControlCard from '../components/ObsControlCard.jsx';
import Loader from '../components/Loader.jsx';
import { api } from '../api/client.js';
import { useAuth } from '../context/AuthContext.jsx';
import { obsControl } from '../lib/obsControl.js';
import { obsRemote } from '../lib/obsRemote.js';
import { obsStage } from '../lib/obsStage.js';

// The OBS deck alone: /<package>/deck, no navbar, no console, the OBS
// Control card as the whole screen (its `deck` mode: the tiles fill it, the
// PC's panels aren't drawn). For an iPad or a phone used as a stream deck
// (the club's ask, 2026-10-28), which cuts through the console tab on the
// OBS PC (src/lib/obsRemote.js); on the PC itself it is the console's OBS
// tab on its own. Added to the home screen it opens without Safari's bars
// (index.html's apple-mobile-web-app-capable).
//
// The page wires OBS as PackageProduction does (the scene map, a connection
// where this is the OBS PC, the remote) and feeds obsStage what the console
// feeds it, from the saved state re-read every few seconds, since another
// console (the PC's) edits it: the tiles' countdowns and the camera layout.
// The stage's automation runs from here too where OBS is connected, as it
// would from the console's tab. The screen is kept awake while the page is
// in front, so the deck never locks mid-show.

const STATE_POLL_MS = 5000;

export default function Deck() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [pkg, setPkg] = useState(null); // null: loading; undefined: none
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setPkg(null);
    setError(null);
    api.myPackages()
      .then((data) => {
        if (cancelled) return;
        setPkg((data?.packages ?? []).find((p) => p.slug === slug) ?? undefined);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e.message);
        setPkg(undefined);
      });
    return () => { cancelled = true; };
  }, [slug]);

  const overlays = useMemo(
    () => (pkg?.overlays ?? []).map((o) => ({ slug: o.slug, name: o.name })),
    [pkg],
  );

  useEffect(() => {
    if (!pkg) return undefined;
    const before = document.title;
    document.title = `${pkg.name} deck · StreamGG`;
    return () => { document.title = before; };
  }, [pkg]);

  useEffect(() => {
    if (!pkg) return;
    obsControl.activateSceneMap(slug, Object.fromEntries(overlays.map((o) => [o.slug, o.name])));
    obsControl.connect();
    obsRemote.configure({ packageSlug: slug });
  }, [pkg, slug, overlays]);

  useEffect(() => {
    if (!pkg) return undefined;
    let cancelled = false;
    let timer = null;
    const read = async () => {
      try {
        const cfg = await api.packageGetConfig(slug);
        if (cancelled) return;
        obsStage.configure({
          packageSlug: slug,
          overlays,
          commentators: cfg?.commentators,
          shared: cfg?.display?.commentator?.shared === true,
          switchAt: cfg?.display?.commentator?.switchAt ?? null,
          startingEndsAt: cfg?.starting?.countdown?.endsAt ?? null,
          brbEndsAt: cfg?.brb?.countdown?.endsAt ?? null,
          serverOffset: typeof cfg?.now === 'number' ? cfg.now - Date.now() : 0,
        });
      } catch {
        // The next read.
      }
      if (!cancelled) timer = setTimeout(read, STATE_POLL_MS);
    };
    read();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [pkg, slug, overlays]);

  // A wake lock lapses when the page leaves the front; asked again when it
  // comes back. Refused or absent, the device locks as it would.
  useEffect(() => {
    let lock = null;
    const acquire = async () => {
      if (!navigator.wakeLock || document.visibilityState !== 'visible') return;
      try {
        lock = await navigator.wakeLock.request('screen');
      } catch {
        lock = null;
      }
    };
    acquire();
    document.addEventListener('visibilitychange', acquire);
    return () => {
      document.removeEventListener('visibilitychange', acquire);
      lock?.release().catch(() => {});
    };
  }, []);

  if (user?.obsExportEnabled === false) {
    return (
      <div className="deck-page deck-page--empty">
        <p>OBS control is off for this account.</p>
        <Link to="/">Dashboard</Link>
      </div>
    );
  }
  if (pkg === null) {
    return (
      <div className="deck-page deck-page--empty">
        <Loader label="Loading…" />
      </div>
    );
  }
  if (!pkg) {
    return (
      <div className="deck-page deck-page--empty">
        <p>{error ?? 'No such package, or none of its overlays are yours.'}</p>
        <Link to="/">Dashboard</Link>
      </div>
    );
  }
  return (
    <div className="deck-page">
      <ObsControlCard deck packageSlug={slug} packageName={pkg.name} overlays={overlays} />
    </div>
  );
}
